import React, {
  useEffect, useRef, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, CircularProgress, Snackbar, Alert,
} from '@mui/material';
import AppShell from '../components/Layout/AppShell';
import PesquisasProntas from '../components/Pesquisa/PesquisasProntas';
import {
  CardWrapper, ButtonUI, ButtonOutlined, Field, PeriodoField,
} from '../components/UI';
import { CAMPOS_PESQUISA } from '../constants/pesquisas.constants';
import * as C from '../theme/colors.constants';
import { buscarInformacoesDashboardService } from '../../services/core/dashboard/dashboard.services';
import { changeKeyHandler, KEY_NUMBER } from '../../helpers/utils/changeEvents.utils';
import { RESULT_NOT_FOUND } from '../../helpers/constants/global.constants';
import { DASHBOARD_ACOES } from '../../helpers/constants/dashboard.constants';
import { pesquisaProcessoController } from '../../controller/pesquisarProcessos.controller';
import {
  findFasesAction, findNegociadoresAction, findEstadosAction, findTiposAgendamentosAction,
  findPastasFaseAction, findStatusProcessosAction,
} from '../../store/Global/LoadSelectors/loadSelectors.saga';
import { pesquisarProcessosUnificadaAction } from '../../store/Processos/PesquisaUnificada/pesquisaProcessosUnificada.saga';
import useDispararPesquisaAtalho from '../hooks/useDispararPesquisaAtalho';
import useBuscarProcessoPorNumero from '../hooks/useBuscarProcessoPorNumero';
import { montarPayloadPesquisa } from './pesquisaProcessosPage.mapper';

/** Cards "Minhas informações", mapeados linha a linha contra `Dashboard.jsx`. */
const CARDS_MINHAS_INFORMACOES = [
  { acao: DASHBOARD_ACOES.MEUS_PROCESSOS, label: 'Meus Processos' },
  { acao: DASHBOARD_ACOES.PROCESSOS_NOVOS, label: 'Processos não trabalhados' },
  { acao: DASHBOARD_ACOES.PROMESSAS_BOLETOS, label: 'Promessas de boletos' },
  { acao: DASHBOARD_ACOES.ACORDOS_NAO_CUMPRIDOS, label: 'Acordos não cumpridos' },
  { acao: DASHBOARD_ACOES.PRAZOS, label: 'Prazos' },
  { acao: DASHBOARD_ACOES.PROMESSAS_RETORNO, label: 'Promessas de retorno' },
];

/**
 * Pesquisa de Processos.
 *
 * Todos os campos ficam sempre visíveis, numa única grid de 2 colunas —
 * não existe mais o toggle "PESQUISA AVANÇADA" do projeto real (que abre um
 * drawer lateral de 500px).
 */
export default function PesquisaProcessosPage() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { disparar, snackbar } = useDispararPesquisaAtalho();
  const { buscar, buscando: buscandoProcesso } = useBuscarProcessoPorNumero();
  // Guarda de "fui eu quem disparou" para os efeitos de resultado/erro logo
  // abaixo de `buscarProcessos`/`buscarProcesso` — ver comentário lá.
  const disparadoPelaPaginaRef = useRef(false);
  const [valores, setValores] = useState({});
  const [pesquisaAtiva, setPesquisaAtiva] = useState(null);
  const [toast, setToast] = useState(null);
  const [toastSeverity, setToastSeverity] = useState('warning');
  const [informacoesDashboard, setInformacoesDashboard] = useState(null);

  const dropdownItems = useSelector(pesquisaProcessoController.getAdvancedSearchDropdownItems);
  const loadingDropdown = useSelector(pesquisaProcessoController.getLoadingDropdown);
  const loadingPesquisa = useSelector(state => state.pesquisaProcessosUnificada.loading);
  const resultadoPesquisa = useSelector(state => state.pesquisaProcessosUnificada.resultado);
  const erroPesquisa = useSelector(state => state.pesquisaProcessosUnificada.erro);
  // true se QUALQUER um dos 6 selectors do controller estiver carregando —
  // inclusive pastasDaFase, nunca disparado por esta página (Pasta depende de
  // Fase, ver Etapa 5 do plano) — spinner não é escopado por campo, decisão
  // de escopo da Etapa 4 (docs/architecture/plano-remocao-mocks-pesquisa-processos.md)
  const carregandoSelectors = loadingDropdown.some(Boolean);

  useEffect(() => {
    buscarInformacoesDashboardService()
      .then((response) => {
        setInformacoesDashboard(response?.data ?? null);
      })
      .catch(() => {
        // Nenhum tratamento — mesmo padrão de Dashboard.jsx: falha de rede
        // deixa os cards em 0 via `?? 0`, sem quebrar a tela.
      });
  }, []);

  useEffect(() => {
    dispatch(findFasesAction());
    dispatch(findNegociadoresAction());
    dispatch(findEstadosAction());
    dispatch(findTiposAgendamentosAction());
    dispatch(findStatusProcessosAction());
  }, [dispatch]);

  const set = id => (valorOuEvento) => {
    const valor = valorOuEvento?.target ? valorOuEvento.target.value : valorOuEvento;
    setValores((v) => {
      const atualizado = { ...v, [id]: valor };
      // Campo cujo dependeDe aponta pra este id fica desabilitado quando o
      // id é limpo (ver renderCampo) — limpar o valor junto evita filtro
      // "fantasma" preenchido enquanto o campo estava visualmente bloqueado.
      if (!valor) {
        CAMPOS_PESQUISA
          .filter(dependente => dependente.dependeDe === id)
          .forEach((dependente) => {
            atualizado[dependente.id] = dependente.tipo === 'periodo' ? {} : '';
          });
      }
      return atualizado;
    });
  };

  const limpar = () => {
    setValores({});
    setPesquisaAtiva(null);
  };

  const mostrarToast = (mensagem, severity = 'warning') => {
    setToastSeverity(severity);
    setToast(mensagem);
  };

  const changeFase = (valorOuEvento) => {
    const novoValorFase = valorOuEvento?.target ? valorOuEvento.target.value : valorOuEvento;
    setValores(v => ({ ...v, fase: novoValorFase, pasta: '' }));
    if (novoValorFase) {
      dispatch(findPastasFaseAction(novoValorFase, 'S'));
    }
  };

  const changeNroProcesso = (e) => {
    changeKeyHandler(e, KEY_NUMBER);
    set('nroProcesso')(e);
  };

  const buscarProcesso = () => buscar(valores.nroProcesso, {
    // Setar o ref DEPOIS do dispatch (dentro de `onEncontrado`, chamado pelo
    // hook logo após `dispatch(setResultadoAction(...))`) continua seguro:
    // o efeito abaixo que lê `disparadoPelaPaginaRef.current` só roda depois
    // que toda esta função síncrona termina de executar — nunca no meio.
    onEncontrado: () => {
      disparadoPelaPaginaRef.current = true;
    },
    onNaoEncontrado: () => mostrarToast(RESULT_NOT_FOUND, 'warning'),
  });

  // `state.pesquisaProcessosUnificada` é global e compartilhada com
  // `useDispararPesquisaAtalho` (cards "Minhas Informações", mesma página) —
  // sem rastrear localmente "fui eu (botão Pesquisar/busca por Nº) quem
  // disparou", os 2 efeitos abaixo reagiriam também ao resultado de um card,
  // navegando/mostrando erro em duplicidade com o próprio efeito do hook
  // (2 `history.push` por 1 clique). `disparadoPelaPaginaRef` resolve isso —
  // mesmo padrão de `disparadoRef` já usado dentro do hook — e também cobre
  // o caso de remontar a página com dado antigo na store (o ref começa
  // `false`, então nada é acionado até um disparo novo desta instância).
  const buscarProcessos = () => {
    disparadoPelaPaginaRef.current = true;
    dispatch(pesquisarProcessosUnificadaAction(montarPayloadPesquisa(valores, pesquisaAtiva)));
  };

  useEffect(() => {
    if (resultadoPesquisa && disparadoPelaPaginaRef.current) {
      disparadoPelaPaginaRef.current = false;
      history.push('/prototipo/processos');
    }
  }, [resultadoPesquisa, history]);

  useEffect(() => {
    if (erroPesquisa && disparadoPelaPaginaRef.current) {
      disparadoPelaPaginaRef.current = false;
      mostrarToast(erroPesquisa, 'error');
    }
  }, [erroPesquisa]);

  const renderCampo = (campo) => {
    if (campo.tipo === 'periodo') {
      return (
        <PeriodoField
          key={campo.id}
          label={campo.label}
          valor={valores[campo.id] || {}}
          onChange={set(campo.id)}
          disabled={campo.dependeDe ? !valores[campo.dependeDe] : undefined}
        />
      );
    }

    if (campo.id === 'nroProcesso') {
      return (
        <Field
          key={campo.id}
          label={campo.label}
          tipo={campo.tipo}
          value={valores[campo.id] || ''}
          onChange={changeNroProcesso}
          disabled={buscandoProcesso}
          onKeyDown={(e) => {
            if (e.key === 'Enter') buscarProcesso();
          }}
          InputProps={buscandoProcesso ? {
            endAdornment: <CircularProgress size={18} />,
          } : undefined}
        />
      );
    }

    const options = campo.selectorKey ? (dropdownItems[campo.selectorKey] || []) : campo.options;

    return (
      <Field
        key={campo.id}
        label={campo.label}
        tipo={campo.tipo}
        options={options}
        value={valores[campo.id] ?? ''}
        onChange={campo.id === 'fase' ? changeFase : set(campo.id)}
        disabled={campo.dependeDe ? !valores[campo.dependeDe] : undefined}
        InputProps={campo.selectorKey && carregandoSelectors ? {
          endAdornment: <CircularProgress size={16} />,
        } : undefined}
      />
    );
  };

  // Layout do print: col.1 (campos), col.2 (campos + UF ao lado), col.3 (botões).
  const col1 = CAMPOS_PESQUISA.filter(c => c.col === 1);
  const col2 = CAMPOS_PESQUISA.filter(c => c.col === 2);

  return (
    <AppShell titulo="Pesquisa de processos">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 1.05vw, 26px)' }}>
        {/* ---------- Bloco 1: Minhas informações ---------- */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              color: C.warmGrey, fontWeight: 700, mb: 1.25, px: 0.5,
            }}
          >
            Minhas informações
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' },
              gap: 'clamp(12px, 1.05vw, 26px)',
            }}
          >
            {CARDS_MINHAS_INFORMACOES.map(c => (
              <Paper
                key={c.acao}
                onClick={() => disparar(c.acao)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 'clamp(10px, 0.7vw, 18px)',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: '0 4px 12px rgba(13,43,82,0.16)' },
                }}
              >
                <Box
                  sx={{
                    minWidth: 'clamp(38px, 2.4vw, 52px)',
                    height: 'clamp(38px, 2.4vw, 52px)',
                    borderRadius: 2,
                    backgroundColor: C.badgeBlue,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.navy,
                    fontWeight: 700,
                    fontSize: 'clamp(16px, 1vw, 24px)',
                  }}
                >
                  {informacoesDashboard?.[c.acao] ?? 0}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 'clamp(13px, 0.78vw, 18px)' }}>
                  {c.label}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* ---------- Bloco 2: Pesquisas prontas ---------- */}
        <PesquisasProntas
          valores={valores}
          onChange={setValores}
          dropdownItems={dropdownItems}
          pesquisaAtiva={pesquisaAtiva}
          onChangePesquisaAtiva={setPesquisaAtiva}
        />

        {/* ---------- Bloco 3: campos de pesquisa + ações ---------- */}
        <CardWrapper>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 1fr) minmax(140px, 0.22fr)' },
              columnGap: 'clamp(16px, 1.6vw, 44px)',
              rowGap: 'clamp(12px, 0.9vw, 22px)',
              alignItems: 'start',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 0.9vw, 22px)' }}>
              {col1.map(renderCampo)}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 0.9vw, 22px)' }}>
              {col2.map(renderCampo)}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 'clamp(10px, 0.8vw, 20px)',
                gridColumn: { lg: '3' },
                gridRow: { lg: '1 / -1' },
                height: { lg: '100%' },
              }}
            >
              <ButtonUI onClick={buscarProcessos} disabled={loadingPesquisa} sx={{ minHeight: 40, gap: 1 }}>
                {loadingPesquisa && <CircularProgress size={16} color="inherit" />}
                {loadingPesquisa ? 'Pesquisando...' : 'Pesquisar'}
              </ButtonUI>
              <ButtonOutlined onClick={limpar} disabled={loadingPesquisa} sx={{ minHeight: 40 }}>
                Limpar
              </ButtonOutlined>
            </Box>
          </Box>
        </CardWrapper>
      </Box>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2200}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toastSeverity} variant="filled" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>

      {/* Snackbar próprio do useDispararPesquisaAtalho (erro dos cards "Minhas
          Informações") — coexiste com o Snackbar acima (erro do formulário/busca
          por Nº) porque o hook não oferece um mecanismo alternativo de erro;
          não é duplicação acidental. */}
      {snackbar}
    </AppShell>
  );
}
